// Background-protection posture self-reported by a beneficiary's phone
// (~hourly via the mobile DeviceHealthWorker). Surfaced by the new backend on
// GET /api/me/beneficiaries/:id as `deviceHealth`. Android-only — iOS phones
// don't report (no Doze/Data-Saver/auto-revoke equivalents), so iOS
// beneficiaries simply have deviceHealth === null and the banner stays hidden.

export interface DeviceHealthStatus {
    ignoringBatteryOptimizations?: boolean
    dataSaverStatus?: 'disabled' | 'whitelisted' | 'enabled' | 'unknown'
    autoRevokeWhitelisted?: boolean
    activityRecognition?: boolean
    backgroundLocation?: boolean
    fineLocation?: boolean
    postNotifications?: boolean
    notificationsEnabled?: boolean
    walkTrackingEnabled?: boolean
    walkServiceRunning?: boolean
    oem?: string
    standbyBucket?: number
    [k: string]: unknown
}

export interface DeviceHealth {
    reportedAt: string | null
    platform: 'android' | 'ios'
    appVersion?: string | null
    appBuild?: number | null
    status: DeviceHealthStatus
}

export interface DeviceHealthIssue {
    label: string
    critical: boolean
}

// Mirror of the mobile `services/deviceProtection.ts → evaluateProtections`.
// Keep the two in sync: same checks, same critical/recommended split.
export function evaluateDeviceHealth(dh: DeviceHealth | null): {
    issues: DeviceHealthIssue[]
    hasCritical: boolean
    stale: boolean
} {
    if (!dh || dh.platform !== 'android') {
        return { issues: [], hasCritical: false, stale: false }
    }
    const s = dh.status || {}
    const issues: DeviceHealthIssue[] = []

    if (s.ignoringBatteryOptimizations === false)
        issues.push({ label: 'Allow WellNuo to run in the background', critical: true })
    if (s.activityRecognition === false)
        issues.push({ label: 'Turn on the Physical activity permission', critical: true })
    if (s.backgroundLocation === false)
        issues.push({ label: 'Allow Location "all the time"', critical: true })
    if (s.dataSaverStatus === 'enabled')
        issues.push({ label: 'Allow unrestricted data', critical: true })
    if (s.autoRevokeWhitelisted === false)
        issues.push({ label: "Turn off “pause app if unused”", critical: true })
    if (s.notificationsEnabled === false || s.postNotifications === false)
        issues.push({ label: 'Turn on notifications', critical: false })
    if (s.walkTrackingEnabled === true && s.walkServiceRunning === false)
        issues.push({ label: 'Walk tracking has stopped — reopen the app', critical: false })

    // Reports arrive ~hourly; a long gap means the app was killed/removed.
    let stale = true
    if (dh.reportedAt) {
        const ageMs = Date.now() - new Date(dh.reportedAt).getTime()
        stale = !(ageMs >= 0 && ageMs < 24 * 60 * 60 * 1000)
    }

    return {
        issues,
        hasCritical: issues.some(i => i.critical),
        stale,
    }
}
